import { useState, useEffect, useCallback } from "react";

import Select, {

  MultiValue,

  StylesConfig,

  components,

  OptionProps,

  MultiValueProps,

  MenuProps,

  MenuListProps,

  InputActionMeta,
  ActionMeta,

} from "react-select";

import { Option } from "../../interface/Option";

import { providerAxiosInstance } from "../../Network/providerAxiosInstance";



interface ProviderOption extends Option {

  type?: string;

  providerName?: string;



  // Business/provider code returned by the backend (Provider_id).

  providerId?: string;



  // SQL database ID used by validate-all.

  dbId?: number;
  is_active?: boolean | number | string;
  status?: string;
  Status?: string;
  __inactive?: boolean;

}



interface ListOfProviderNameProps {

  onChange: (selected: ProviderOption[]) => void;

  clear?: boolean;

  defaultValue?: ProviderOption[];

  qaIdPrefix?: string;

}



// Only explicit inactive status is treated as inactive. Missing status is not guessed.
const isInactiveProvider = (item: any): boolean => {
  const status = item?.__inactive === true ? false :
    item?.is_active ?? item?.Is_active ?? item?.isActive ?? item?.status ?? item?.Status;
  return status === false || status === 0 || status === "0" ||
    (typeof status === "string" && ["inactive", "disabled", "false", "0"].includes(status.trim().toLowerCase()));
};
const providerKey = (item: ProviderOption) => String(item.dbId ?? item.value ?? "");
const mergeProviderStatus = (item: ProviderOption, source?: ProviderOption): ProviderOption => ({
  ...item,
  __inactive: isInactiveProvider(item) || (!!source && isInactiveProvider(source)),
});

const DEFAULT_QA_PREFIX = "list-of-provider-name";



const qaSafe = (value: string | number | undefined | null) =>

  String(value ?? "unknown")

    .trim()

    .toLowerCase()

    .replace(/[^a-z0-9]+/g, "-")

    .replace(/^-+|-+$/g, "");



const buildQaId = (prefix: string, suffix: string | number) =>

  `${qaSafe(prefix)}-${qaSafe(suffix)}`;



const qaAttrs = (prefix: string, suffix: string | number) => {

  const id = buildQaId(prefix, suffix);



  return {

    id,

    "data-testid": id,

    "data-qa": id,

  };

};



// Fetch current source statuses on each mount; don't keep stale active results globally.
const fetchAllProviders = async (): Promise<ProviderOption[]> => {
  const response = await providerAxiosInstance.get("getProvider");
  const rawResult = response.data?.result ?? response.data?.data ?? [];
  const data: any[] = Array.isArray(rawResult) ? rawResult :
    Array.isArray(rawResult?.data) ? rawResult.data : [];

  return data.map((item: any): ProviderOption | null => {
    const dbId = item?.id ?? item?.provider_db_id ?? item?.providerDbId;
    const businessProviderId = item?.Provider_id ?? item?.provider_id ?? item?.providerId ?? dbId;
    const providerName = String(
      item?.provider_name ?? item?.Provider_name ?? item?.providerName ?? item?.name ?? ""
    ).trim();
    if (dbId === null || dbId === undefined || !providerName) return null;
    return {
      value: String(dbId),
      label: `(${businessProviderId}) ${providerName}`,
      type: item?.type ?? item?.Type,
      providerName,
      providerId: String(businessProviderId),
      dbId: Number(dbId),
      is_active: item?.is_active ?? item?.Is_active ?? item?.isActive,
      status: item?.status ?? item?.Status,
      __inactive: isInactiveProvider(item),
    };
  }).filter((item: ProviderOption | null): item is ProviderOption => item !== null);
};

const ListOfProviderName = ({

  onChange,

  clear,

  defaultValue,

  qaIdPrefix = DEFAULT_QA_PREFIX,

}: ListOfProviderNameProps) => {

  const [options, setOptions] = useState<ProviderOption[]>([]);

  const [allOptions, setAllOptions] = useState<ProviderOption[]>([]);

  const [selectedOptions, setSelectedOptions] = useState<ProviderOption[]>([]);

  const [loading, setLoading] = useState(false);

  const [inputValue, setInputValue] = useState<string>("");



  const selectQaId = buildQaId(qaIdPrefix, "select");

  const inputQaId = buildQaId(qaIdPrefix, "input");



  const loadAllOptions = useCallback(async () => {

    setLoading(true);

    try {

      const formattedOptions = await fetchAllProviders();

      setAllOptions(formattedOptions);

      setOptions(formattedOptions.filter((item) => !isInactiveProvider(item)));

    } catch (error) {

      console.error("Error loading provider options:", error);

      setAllOptions([]);

      setOptions([]);

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    void loadAllOptions();

  }, [loadAllOptions]);



  useEffect(() => {

    const query = inputValue.trim().toLowerCase();



    if (!query) {

      setOptions(allOptions.filter((item) => !isInactiveProvider(item)));

      return;

    }



    // Name-only filtering. Provider ID/code and DB ID are not searchable here.

    setOptions(

      allOptions.filter((option) =>

        !isInactiveProvider(option) &&
        String(option.providerName ?? "").toLowerCase().includes(query)

      )

    );

  }, [inputValue, allOptions]);



  // React-select is controlled: never allow a previously linked inactive item to be removed.
  const handleChange = (
    selected: MultiValue<ProviderOption>,
    _actionMeta: ActionMeta<ProviderOption>
  ) => {
    const requested = [...selected];
    const previous = selectedOptions;
    const locked = previous.filter(isInactiveProvider);
    const next = requested
      .filter((item) => !isInactiveProvider(item) || previous.some(p => providerKey(p) === providerKey(item)))
      .map((item) => mergeProviderStatus(
        item,
        previous.find(p => providerKey(p) === providerKey(item)) ??
          allOptions.find(p => providerKey(p) === providerKey(item))
      ));
    locked.forEach((item) => {
      if (!next.some(other => providerKey(other) === providerKey(item))) next.push(item);
    });
    setSelectedOptions(next);
    onChange(next);
  };

  useEffect(() => {
    if (!clear) return;
    // A new/empty form can reset fully. A linked inactive item in Edit stays locked.
    setSelectedOptions(previous =>
      defaultValue?.length ? previous.filter(isInactiveProvider) : []
    );
  }, [clear]);

  useEffect(() => {
    if (!defaultValue) return;
    setSelectedOptions(defaultValue.map(item => {
      const source = allOptions.find(p => providerKey(p) === providerKey(item) ||
        (!!p.providerId && p.providerId === item.providerId));
      return mergeProviderStatus(item, source);
    }));
  }, [defaultValue, allOptions]);

  const handleInputChange = (

    value: string,

    actionMeta: InputActionMeta

  ): string => {

    // Only actual typing should update the search value.

    if (actionMeta.action === "input-change") {

      setInputValue(value);

    }



    return value;

  };



  const formatOptionLabel = (option: ProviderOption) => (

    <div className="flex justify-between items-center w-full">

      <span>{option.label}</span>



      {option.type && (

        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded ml-2">

          ({option.type})

        </span>

      )}

    </div>

  );



  const customStyles: StylesConfig<ProviderOption, true> = {

    control: (styles) => ({

      ...styles,

      minHeight: "40px",

      fontSize: "14px",

      borderRadius: "8px",

      cursor: "pointer",

    }),



    valueContainer: (styles) => ({

      ...styles,

      fontSize: "14px",

    }),



    input: (styles) => ({

      ...styles,

      fontSize: "14px",

    }),



    placeholder: (styles) => ({

      ...styles,

      fontSize: "14px",

      color: "#6B7280",

    }),



    multiValue: (styles, { data }) => ({

      ...styles,

      backgroundColor: isInactiveProvider(data) ? "#cbd5e1" : "#007BFF",

      border: isInactiveProvider(data) ? "1px solid #94a3b8" : "1px solid transparent",

      color: isInactiveProvider(data) ? "#334155" : "#FFFFFF",

      borderRadius: "5px",

    }),



    multiValueLabel: (styles, { data }) => ({

      ...styles,

      color: isInactiveProvider(data) ? "#334155" : "#FFFFFF",

      fontWeight: isInactiveProvider(data) ? 600 : undefined,

    }),



    multiValueRemove: (styles) => ({

      ...styles,

      color: "#FFFFFF",

      cursor: "pointer",

      ":hover": {

        backgroundColor: "#0056b3",

        color: "#FFFFFF",

      },

    }),



    option: (styles, { isSelected, isFocused }) => ({

      ...styles,

      backgroundColor: isSelected

        ? "#007BFF"

        : isFocused

          ? "#E6F0FA"

          : "#FFFFFF",

      color: isSelected ? "#FFFFFF" : "#000000",

      fontSize: "14px",

      padding: "8px 10px",

      cursor: "pointer",

      ":hover": {

        backgroundColor: "#0056b3",

        color: "#FFFFFF",

      },

    }),



    menu: (styles) => ({

      ...styles,

      zIndex: 999999,

      pointerEvents: "auto",

    }),



    menuPortal: (styles) => ({

      ...styles,

      zIndex: 999999,

      pointerEvents: "auto",

    }),



    menuList: (styles) => ({

      ...styles,

      maxHeight: "220px",

      overflowY: "auto",

      pointerEvents: "auto",

    }),

  };



  const Menu = (props: MenuProps<ProviderOption, true>) => {

    const qa = qaAttrs(qaIdPrefix, "menu");



    return (

      <components.Menu

        {...props}

        innerProps={{

          ...(props.innerProps || {}),

          ...qa,

        }}

      />

    );

  };



  const MenuList = (props: MenuListProps<ProviderOption, true>) => (

    <components.MenuList {...props}>

      {props.children}

    </components.MenuList>

  );



  const OptionComponent = (props: OptionProps<ProviderOption, true>) => {

    const qa = qaAttrs(

      qaIdPrefix,

      `option-${qaSafe(String(props.data.value))}`

    );



    return (

      <components.Option

        {...props}

        innerProps={{

          ...props.innerProps,

          ...qa,

          "data-option-label": props.data.label,

          "data-option-value": props.data.value,

          "data-provider-db-id": props.data.dbId ?? props.data.value,

          "data-provider-id": props.data.providerId || "",

          "data-provider-name": props.data.providerName || "",

          "data-provider-type": props.data.type || "",

        } as any}

      />

    );

  };



  const MultiValueRemove = (props: any) =>
    isInactiveProvider(props.data) ? null : <components.MultiValueRemove {...props} />;

  const MultiValueComponent = (

    props: MultiValueProps<ProviderOption, true>

  ) => (

    <div

      {...qaAttrs(

        qaIdPrefix,

        `selected-${qaSafe(String(props.data.value))}`

      )}

      data-selected-label={props.data.label}

      data-selected-value={props.data.value}

      data-provider-db-id={

        props.data.dbId ?? props.data.value

      }

      data-provider-id={

        props.data.providerId || ""

      }

      data-provider-name={

        props.data.providerName || ""

      }

      data-provider-type={

        props.data.type || ""

      }

    >

      <components.MultiValue {...props} />

    </div>

  );



  return (

    <div {...qaAttrs(qaIdPrefix, "wrapper")}>

      <Select

        styles={{

          ...customStyles,

          menu: (base: any) => ({

            ...base,

            zIndex: 999999,

            pointerEvents: "auto",

          }),

          menuPortal: (base: any) => ({

            ...base,

            zIndex: 999999,

            pointerEvents: "auto",

          }),

          menuList: (base: any) => ({

            ...base,

            maxHeight: 260,

            overflowY: "auto",

            pointerEvents: "auto",

            WebkitOverflowScrolling: "touch",

          }),

        }}









        isMulti

        inputId={inputQaId}

        instanceId={selectQaId}

        name={selectQaId}

        classNamePrefix={selectQaId}

        className={selectQaId}

        aria-label="Provider selector"

        data-testid={selectQaId}

        data-qa={selectQaId}

        value={selectedOptions}

        isClearable={!selectedOptions.some(isInactiveProvider)}

        onChange={handleChange}

        options={options.filter(option => !isInactiveProvider(option))}

        isLoading={loading}

        onInputChange={handleInputChange}

        formatOptionLabel={formatOptionLabel}

        placeholder="Type to search or select from list..."

        noOptionsMessage={() =>

          loading ? "Loading..." : "No providers found"

        }



        components={{

          Menu,

          MenuList,

          Option: OptionComponent,

          MultiValue: MultiValueComponent,

          MultiValueRemove,

        }}

        menuPortalTarget={

          typeof document !== "undefined" ? document.body : undefined

        }

        menuPosition="fixed"

        menuPlacement="auto"

        menuShouldScrollIntoView={false}

        closeMenuOnSelect={false}

        blurInputOnSelect={false}

        captureMenuScroll={true}

      />



      <span

        {...qaAttrs(qaIdPrefix, "loading-state")}

        data-loading={loading ? "true" : "false"}

        style={{ display: "none" }}

      >

        {loading ? "Loading" : "Idle"}

      </span>



      <span

        {...qaAttrs(qaIdPrefix, "selected-count")}

        data-count={selectedOptions.length}

        style={{ display: "none" }}

      >

        {selectedOptions.length}

      </span>



      <span

        {...qaAttrs(qaIdPrefix, "options-count")}

        data-count={options.length}

        style={{ display: "none" }}

      >

        {options.length}

      </span>

    </div>

  );

};



export default ListOfProviderName;


